import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, ChevronRight, Phone, MapPin, ShieldAlert } from "lucide-react";
import { api } from "../lib/api";

/**
 * Compact homepage strip for Active Cases ("HELP IDENTIFY").
 * Renders at most 3 "Help Identify" cases. If none exist, the whole
 * section is hidden — we never show an empty "ACTIVE CASES" header
 * because that would look bad when the registry has no open cases.
 */
export default function ActiveCasesStrip() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/active-cases", { params: { status: "Help Identify", limit: 3 } });
        if (!cancelled) setItems(data.items || []);
      } catch { /* no-op; strip just hides */ }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading || items.length === 0) return null;

  return (
    <section className="max-w-7xl mx-auto px-6 lg:px-8 mt-12" data-testid="home-active-cases-strip">
      <div className="border-2 border-red-700 bg-red-600 text-white px-6 py-5 flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="text-3xl leading-none">🚨</div>
          <div>
            <div className="font-display font-black text-xl sm:text-2xl tracking-tight leading-tight">
              ACTIVE CASES — HELP IDENTIFY
            </div>
            <div className="text-sm text-red-100 mt-1">
              Recognize a person or vehicle? Help law enforcement protect animals.
            </div>
          </div>
        </div>
        <Link
          to="/active-cases"
          className="inline-flex items-center gap-1 bg-white text-zinc-900 font-black uppercase tracking-widest text-xs px-4 py-2 border-2 border-white hover:bg-zinc-100"
          data-testid="home-active-cases-view-all"
        >
          View all <ChevronRight size={14}/>
        </Link>
      </div>

      <div className="mt-5 grid grid-cols-1 md:grid-cols-3 gap-5">
        {items.map((c) => (
          <Link
            key={c.id}
            to={`/active-cases/${c.id}`}
            className="border-2 border-zinc-900 bg-white hover-shadow-harsh flex flex-col"
            data-testid={`home-active-case-${c.id}`}
          >
            <div className="relative aspect-[16/10] bg-zinc-100 overflow-hidden border-b-2 border-zinc-900">
              {c.photo_url ? (
                <img src={c.photo_url} alt={c.title} className="w-full h-full object-cover" loading="lazy" referrerPolicy="no-referrer" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-zinc-400">
                  <ShieldAlert size={32}/>
                </div>
              )}
              <div className="absolute top-2 left-2 bg-red-600 text-white px-2 py-1 text-[10px] font-black uppercase tracking-widest border border-red-800 inline-flex items-center gap-1">
                <AlertTriangle size={10}/> HELP IDENTIFY
              </div>
            </div>
            <div className="p-4 flex-1 flex flex-col">
              <div className="label-eyebrow text-zinc-500 flex items-center gap-1">
                <MapPin size={10}/> {c.city}, {c.state_code || c.state}
              </div>
              <h3 className="font-display font-black text-base leading-tight mt-1">{c.title}</h3>
              <p className="text-xs text-zinc-700 mt-2 line-clamp-2 leading-relaxed">{c.summary}</p>
              <div className="mt-auto pt-3 flex items-center justify-between text-[11px] font-mono text-zinc-600">
                <span className="inline-flex items-center gap-1"><Phone size={10}/> {c.agency_phone || c.agency_name}</span>
                <span className="inline-flex items-center gap-1 text-red-700">View case <ChevronRight size={12}/></span>
              </div>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}
